// Auth persistence for the new submission flow.
//
// The account is created at email-code verification, which is where the JWT is
// issued — every step after that (materials upload, phone verification, draft
// submission) is an authenticated call. Kept in sessionStorage (per-tab, like
// the flow state itself) and NEVER in the 7-day tData tracking cookie.

const KEY = "sfAuth.v1";

export interface ISubmissionAuth {
  clientId: number;
  jwt: string;
  refreshToken?: string;
  expiresAtUtc?: string;
  // The language saved on the account (an i18n key, "fr"), when the classic login signed them in:
  // it wins over the browser for this tab (flowLanguage.applySignedInLanguage).
  language?: string;
  // Who is signed in, for "Signed in as" at the bottom of steps 2–4 (read once, then kept here).
  firstName?: string;
  lastName?: string;
}

export const setSubmissionAuth = (auth: {
  clientId: number;
  jwt: string;
  refreshToken?: string;
  expiresIn?: number;
  language?: string;
  firstName?: string;
  lastName?: string;
}) => {
  try {
    const stored: ISubmissionAuth = {
      clientId: auth.clientId,
      jwt: auth.jwt,
      refreshToken: auth.refreshToken,
      expiresAtUtc: auth.expiresIn
        ? new Date(Date.now() + auth.expiresIn * 1000).toISOString()
        : undefined,
      language: auth.language,
      firstName: auth.firstName,
      lastName: auth.lastName,
    };
    window.sessionStorage.setItem(KEY, JSON.stringify(stored));
  } catch (e) {
    // Storage unavailable — calls in this page still carry the in-memory token
    // via the model; a refresh will send the user back to verification.
  }
};

export const getSubmissionAuth = (): ISubmissionAuth | null => {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as ISubmissionAuth) : null;
  } catch (e) {
    return null;
  }
};

export const clearSubmissionAuth = () => {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch (e) {
    // ignore
  }
};

// Authorization header for authenticated flow calls. The stored jwt already
// carries the "Bearer " prefix (matching ActorSignupResponseDto.Jwt).
export const getSubmissionAuthHeader = (): { [key: string]: string } => {
  const auth = getSubmissionAuth();
  return auth && auth.jwt ? { Authorization: auth.jwt } : {};
};

// A token past its expiry (about 21 hours, and there's no refresh) counts as signed out, so
// the visitor is offered the login instead of a step whose calls all fail.
export const isSubmissionAuthed = (): boolean => {
  const auth = getSubmissionAuth();
  if (!auth || !auth.jwt || !auth.clientId) return false;
  const exp = Number(tokenClaims(auth.jwt).exp);
  return !(Number.isFinite(exp) && exp > 0 && exp * 1000 <= Date.now());
};

// The token's payload. Read only; nothing is verified here (the APIs do that).
export const tokenClaims = (jwt: string | null | undefined): { [claim: string]: any } => {
  if (!jwt) return {};
  try {
    const part = jwt.replace(/^Bearer\s+/i, "").split(".")[1] || "";
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "===".slice((base64.length + 3) % 4);
    const payload = JSON.parse(window.atob(padded));
    return payload && typeof payload === "object" ? payload : {};
  } catch (e) {
    return {};
  }
};

// The classic site's session key, carried in the flow's token (its WebSessionKey claim). The
// membership site takes it as ?k= (as the legacy signup's redirects do), so "Go PRO" lands the
// actor there signed in.
export const getSubmissionWebSessionKey = (): string => {
  const auth = getSubmissionAuth();
  const key = auth ? tokenClaims(auth.jwt).WebSessionKey : "";
  return typeof key === "string" ? key : "";
};

// The actor a token from the classic login belongs to: their client id, or 0 for anyone else (an
// agent, UserType 3, or a casting account, 2: the flow submits as an actor only).
export const actorClientIdOf = (accessToken: string): number => {
  const claims = tokenClaims(accessToken);
  const clientId = Number(claims.UserId);
  return Number.isInteger(clientId) && clientId > 0 && String(claims.UserType) === "8" ? clientId : 0;
};

// What the account says about itself (auth/user/me), kept with the tab's sign-in.
export interface ISubmissionAccountInfo {
  language?: string;
  firstName?: string;
  lastName?: string;
}

// The seconds a token has left, from its exp claim (undefined when it has none).
export const secondsLeftOf = (accessToken: string): number | undefined => {
  const exp = Number(tokenClaims(accessToken).exp);
  return Number.isFinite(exp) && exp > 0 ? Math.max(0, Math.floor(exp - Date.now() / 1000)) : undefined;
};

// A token from the classic login (the jwt/bridge hand-off, or its session) becomes the flow's
// sign-in. Only an actor's token is taken. Returns the client id, or 0 when the token isn't usable.
export const setSubmissionAuthFromToken = (accessToken: string, account: ISubmissionAccountInfo = {}): number => {
  const clientId = actorClientIdOf(accessToken);
  if (!clientId) return 0;
  setSubmissionAuth({
    clientId,
    jwt: `Bearer ${accessToken.replace(/^Bearer\s+/i, "")}`,
    expiresIn: secondsLeftOf(accessToken),
    ...account,
  });
  return clientId;
};

// The signed-in account's name, when this tab knows it.
export const getSubmissionAccountName = (): { firstName: string; lastName: string } | null => {
  const auth = getSubmissionAuth();
  return auth && auth.firstName ? { firstName: auth.firstName, lastName: auth.lastName || "" } : null;
};

// Keeps the name read later (an account made by the flow's own signup: the name was typed on step 1).
export const rememberSubmissionAccountName = (firstName: string, lastName: string) => {
  const auth = getSubmissionAuth();
  if (!auth) return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify({ ...auth, firstName, lastName }));
  } catch (e) {
    // The name is asked again on the next page.
  }
};

// The account's saved language, for a tab signed in through the classic login (null otherwise).
export const getSubmissionLanguage = (): string | null => {
  const auth = getSubmissionAuth();
  return auth && auth.language && isSubmissionAuthed() ? auth.language : null;
};

// Who was signed in here, even after the token expired: their Workbook ID (the client id)
// pre-fills the login.
export const getSubmissionLoginHint = (): number | null => {
  const auth = getSubmissionAuth();
  return auth && auth.clientId ? auth.clientId : null;
};
